import { getModelLabel } from './formHelpers';

export const SLOT_COLOR_PRESETS = {
  empty: { stroke: '#b8c99a', fill: '#f4ffe6' },
  inStock: { stroke: '#1e7e34', fill: '#28a745' },
  contracted: { stroke: '#bd2130', fill: '#dc3545' },
  closed: { stroke: '#000000', fill: '#FF6347' },
  office: { stroke: '#d4a800', fill: '#ffc107' }
};

export const SCHEMATIC_LEGEND = [
  { key: 'empty', label: 'Empty Slot' },
  { key: 'inStock', label: 'In Stock' },
  { key: 'contracted', label: 'Contracted' },
  { key: 'closed', label: 'Closed' },
  { key: 'office', label: 'Office Building' }
];

const getCurtailmentCutOffDate = () => {
  const today = new Date();
  today.setFullYear(today.getFullYear() - 1);
  today.setMonth(today.getMonth() + 1);
  today.setDate(0);
  today.setHours(23, 59, 59, 999);
  return today.getTime();
};

export const getSlotColors = slot => {
  if (slot?.status === 'Closed') return SLOT_COLOR_PRESETS.closed;
  if (slot?.inventory?.contract) return SLOT_COLOR_PRESETS.contracted;
  if (slot?.inventory) return SLOT_COLOR_PRESETS.inStock;
  if (slot?.isOfficeBuilding) return SLOT_COLOR_PRESETS.office;
  return SLOT_COLOR_PRESETS.empty;
};

export const isEmptySlot = slot =>
  !slot?.inventory && !slot?.isOfficeBuilding && slot?.status !== 'Closed';

const SLOT_NUMBER_FONT = {
  min: 6,
  max: 9,
  minorAxisRatio: 0.15,
  cornerWidthRatio: 0.4,
  cornerHeightRatio: 0.22
};

export const computeSlotNumberFontSize = (slotNumber, labelFrame) => {
  if (!slotNumber || !labelFrame?.lineWidth || !labelFrame?.stackHeight) {
    return null;
  }

  const minorAxis = Math.min(labelFrame.lineWidth, labelFrame.stackHeight);
  const targetSize = Math.min(
    SLOT_NUMBER_FONT.max,
    Math.max(SLOT_NUMBER_FONT.min, minorAxis * SLOT_NUMBER_FONT.minorAxisRatio)
  );

  const fitted = fitFontSizeToBox(
    [slotNumber],
    minorAxis * SLOT_NUMBER_FONT.cornerWidthRatio,
    minorAxis * SLOT_NUMBER_FONT.cornerHeightRatio,
    {
      minFontSize: SLOT_NUMBER_FONT.min,
      maxFontSize: targetSize
    }
  );

  return fitted.fontSize;
};

/** Body lines only — mirrors the live map overlay fields (without the # badge). */
export const getSlotBodyLabelLines = slot => {
  if (slot?.isOfficeBuilding && !slot?.inventory) {
    return ['Office'];
  }

  const inventory = slot?.inventory;
  if (!inventory) {
    return slot?.isOfficeBuilding ? [] : ['Empty'];
  }

  const lines = [];

  if (inventory.serialA) {
    lines.push(String(inventory.serialA));
  }

  const manufacturer = inventory.manufacturer?.factoryShortName || '';
  const model = getModelLabel(inventory.model);
  const modelLine = manufacturer ? `${manufacturer} - ${model}` : model;
  if (modelLine && modelLine !== '-') {
    lines.push(modelLine);
  }

  lines.push(`B/B: ${inventory.bedRooms ?? '-'}/${inventory.bathRooms ?? '-'}`);

  if (inventory.contract?.buyer) {
    lines.push(String(inventory.contract.buyer));
  }

  if (
    inventory.invoiceDate &&
    new Date(inventory.invoiceDate).getTime() < getCurtailmentCutOffDate()
  ) {
    lines.push('Curtailment');
  }

  return lines;
};

export const getSlotLabelLines = slot => {
  const lines = [];

  if (slot?.number != null && slot.number !== '') {
    lines.push(`#${slot.number}`);
  }

  return [...lines, ...getSlotBodyLabelLines(slot)];
};

export const computeGeoBounds = slots => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  (slots || []).forEach(slot => {
    (slot?.paths || []).forEach(({ lat, lng }) => {
      if (lat == null || lng == null) return;
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    });
  });

  if (!Number.isFinite(minLat)) {
    return { minLat: 0, maxLat: 1, minLng: 0, maxLng: 1 };
  }

  return { minLat, maxLat, minLng, maxLng };
};

const projectLatLng = (lat, lng, bounds, viewWidth, viewHeight, padding) => {
  const lngSpan = bounds.maxLng - bounds.minLng || 1e-8;
  const latSpan = bounds.maxLat - bounds.minLat || 1e-8;
  const innerWidth = viewWidth - padding * 2;
  const innerHeight = viewHeight - padding * 2;

  return {
    x: padding + ((lng - bounds.minLng) / lngSpan) * innerWidth,
    y: padding + ((bounds.maxLat - lat) / latSpan) * innerHeight
  };
};

export const computePolygonBBox = points => {
  if (!points?.length) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0, centerX: 0, centerY: 0 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  points.forEach(({ x, y }) => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });

  const width = maxX - minX;
  const height = maxY - minY;

  return {
    minX,
    minY,
    maxX,
    maxY,
    width,
    height,
    centerX: minX + width / 2,
    centerY: minY + height / 2
  };
};

const normalizeReadableAngle = degrees => {
  let angle = degrees;
  while (angle <= -90) angle += 180;
  while (angle > 90) angle -= 180;
  return angle;
};

export const localToScreen = (localX, localY, frame) => {
  const cos = Math.cos(frame.angleRad);
  const sin = Math.sin(frame.angleRad);
  return {
    x: frame.cx + localX * cos - localY * sin,
    y: frame.cy + localX * sin + localY * cos
  };
};

/**
 * Builds a label frame aligned to the polygon's principal axes.
 * Local +X is the direction text lines extend; local +Y is the line-stacking direction.
 */
export const computePolygonLabelFrame = points => {
  if (!points?.length) {
    return {
      cx: 0,
      cy: 0,
      angleRad: 0,
      rotationDeg: 0,
      lineWidth: 0,
      stackHeight: 0,
      localMinX: 0,
      localMinY: 0,
      localMaxX: 0,
      localMaxY: 0
    };
  }

  const cx = points.reduce((sum, point) => sum + point.x, 0) / points.length;
  const cy = points.reduce((sum, point) => sum + point.y, 0) / points.length;

  let sxx = 0;
  let syy = 0;
  let sxy = 0;

  points.forEach(({ x, y }) => {
    const dx = x - cx;
    const dy = y - cy;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  });

  const angleRad = Math.atan2(2 * sxy, sxx - syy) / 2;
  const cos = Math.cos(-angleRad);
  const sin = Math.sin(-angleRad);

  let localMinX = Infinity;
  let localMaxX = -Infinity;
  let localMinY = Infinity;
  let localMaxY = -Infinity;

  points.forEach(({ x, y }) => {
    const dx = x - cx;
    const dy = y - cy;
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;
    localMinX = Math.min(localMinX, localX);
    localMaxX = Math.max(localMaxX, localX);
    localMinY = Math.min(localMinY, localY);
    localMaxY = Math.max(localMaxY, localY);
  });

  return {
    cx,
    cy,
    angleRad,
    rotationDeg: normalizeReadableAngle((angleRad * 180) / Math.PI),
    lineWidth: localMaxX - localMinX,
    stackHeight: localMaxY - localMinY,
    localMinX,
    localMinY,
    localMaxX,
    localMaxY
  };
};

export const computeSlotNumberPosition = labelFrame => {
  const corners = [
    [labelFrame.localMinX, labelFrame.localMinY],
    [labelFrame.localMinX, labelFrame.localMaxY],
    [labelFrame.localMaxX, labelFrame.localMinY],
    [labelFrame.localMaxX, labelFrame.localMaxY]
  ].map(([localX, localY]) => localToScreen(localX, localY, labelFrame));

  const topLeftCorner = corners.reduce((best, point) => {
    if (!best) return point;
    if (point.y < best.y - 0.5) return point;
    if (Math.abs(point.y - best.y) <= 0.5 && point.x < best.x) return point;
    return best;
  }, null);

  const inset = Math.max(2.5, Math.min(labelFrame.lineWidth, labelFrame.stackHeight) * 0.07);
  const towardCenterX = labelFrame.cx - topLeftCorner.x;
  const towardCenterY = labelFrame.cy - topLeftCorner.y;
  const distance = Math.hypot(towardCenterX, towardCenterY) || 1;

  return {
    x: topLeftCorner.x + (towardCenterX / distance) * inset,
    y: topLeftCorner.y + (towardCenterY / distance) * inset
  };
};

let measureCanvasContext = null;

const getMeasureContext = () => {
  if (typeof document === 'undefined') return null;
  if (!measureCanvasContext) {
    const canvas = document.createElement('canvas');
    measureCanvasContext = canvas.getContext('2d');
  }
  return measureCanvasContext;
};

const wrapLine = (text, maxWidth, ctx) => {
  if (!text) return [];
  if (ctx.measureText(text).width <= maxWidth) return [text];

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    const wrapped = [];
    let current = '';

    words.forEach(word => {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) {
        current = candidate;
        return;
      }

      if (current) wrapped.push(current);
      current = word;
    });

    if (current) wrapped.push(current);
    if (wrapped.length) return wrapped;
  }

  let chunk = '';
  const wrapped = [];
  [...text].forEach(char => {
    const candidate = chunk + char;
    if (ctx.measureText(candidate).width <= maxWidth) {
      chunk = candidate;
      return;
    }

    if (chunk) wrapped.push(chunk);
    chunk = char;
  });

  if (chunk) wrapped.push(chunk);
  return wrapped.length ? wrapped : [text];
};

const wrapLines = (lines, maxWidth, fontSize, fontFamily) => {
  const ctx = getMeasureContext();
  if (!ctx) return lines;

  ctx.font = `600 ${fontSize}px ${fontFamily}`;
  return lines.flatMap(line => wrapLine(line, maxWidth, ctx));
};

export const fitFontSizeToBox = (lines, maxWidth, maxHeight, options = {}) => {
  const {
    minFontSize = 3,
    maxFontSize = 16,
    lineHeight = 1.2,
    fontFamily = 'Arial, Helvetica, sans-serif'
  } = options;

  if (!lines?.length || maxWidth <= 0 || maxHeight <= 0) {
    return { fontSize: minFontSize, lines: lines || [] };
  }

  const ctx = getMeasureContext();
  if (!ctx) {
    return { fontSize: 10, lines };
  }

  for (let fontSize = maxFontSize; fontSize >= minFontSize; fontSize -= 0.5) {
    const wrappedLines = wrapLines(lines, maxWidth, fontSize, fontFamily);
    const lineHeightPx = fontSize * lineHeight;
    const totalHeight = wrappedLines.length * lineHeightPx;
    const allLinesFit = wrappedLines.every(line => ctx.measureText(line).width <= maxWidth);

    if (allLinesFit && totalHeight <= maxHeight) {
      return { fontSize, lines: wrappedLines };
    }
  }

  const wrappedLines = wrapLines(lines, maxWidth, minFontSize, fontFamily);
  return { fontSize: minFontSize, lines: wrappedLines };
};

export const prepareSchematicSlots = (slots, options = {}) => {
  const viewWidth = options.viewWidth ?? 1000;
  const validSlots = (slots || []).filter(slot => slot?.paths?.length);

  const bounds = computeGeoBounds(validSlots);
  const lngSpan = bounds.maxLng - bounds.minLng || 1e-8;
  const latSpan = bounds.maxLat - bounds.minLat || 1e-8;
  const viewHeight =
    options.viewHeight ?? Math.max(320, viewWidth * (latSpan / lngSpan));
  const padding =
    options.padding ??
    (options.viewHeight
      ? Math.round(Math.min(viewWidth, viewHeight) * 0.035)
      : 48);

  const preparedSlots = validSlots.map(slot => {
    const points = slot.paths.map(({ lat, lng }) =>
      projectLatLng(lat, lng, bounds, viewWidth, viewHeight, padding)
    );
    const labelFrame = computePolygonLabelFrame(points);
    const colors = getSlotColors(slot);
    const slotNumber =
      slot?.number != null && slot.number !== '' ? `#${slot.number}` : null;
    const bodyLines = getSlotBodyLabelLines(slot);
    const labelPadding = Math.max(
      2,
      Math.min(labelFrame.lineWidth, labelFrame.stackHeight) * 0.08
    );
    const emptySlot = isEmptySlot(slot);
    const numberBand = Math.min(labelFrame.stackHeight * 0.18, 12);
    const fitWidth = Math.max(0, labelFrame.lineWidth - labelPadding * 2);
    const fitHeight = Math.max(0, labelFrame.stackHeight - labelPadding * 2 - numberBand);

    const fittedBody =
      bodyLines.length > 0
        ? fitFontSizeToBox(bodyLines, fitWidth, fitHeight, {
            maxFontSize: emptySlot ? 8 : 14,
            minFontSize: 3
          })
        : { fontSize: emptySlot ? 6 : 12, lines: [] };

    const bodyFontSize = emptySlot
      ? Math.max(4, fittedBody.fontSize / 2)
      : fittedBody.fontSize;
    const numberFontSize = computeSlotNumberFontSize(slotNumber, labelFrame);

    const cornerPosition = computeSlotNumberPosition(labelFrame);

    return {
      slot,
      points,
      labelFrame,
      colors,
      slotNumber,
      bodyLines: fittedBody.lines,
      bodyFontSize,
      numberFontSize,
      cornerPosition,
      pathD: points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ') + ' Z'
    };
  });

  return {
    viewWidth,
    viewHeight,
    slots: preparedSlots
  };
};
